import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X, FileText, Image as ImageIcon, Plus, GripVertical } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { validateFiles, formatFileSize, isImageFile, MAX_FILES } from "@/lib/file-utils";
import { useToast } from "@/hooks/use-toast";

interface Props {
  files: File[];
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  /** Accept string for input */
  accept?: string;
  /** Label shown in empty state */
  label?: string;
  /** Compact mode for smaller dialogs */
  compact?: boolean;
  /** Allow camera capture on mobile */
  capture?: boolean;
}

export default function MultiFileUploader({
  files,
  onFilesChange,
  maxFiles = MAX_FILES,
  accept = "image/*,.pdf,.doc,.docx",
  label = "اسحب الملفات هنا أو اضغط للرفع",
  compact = false,
  capture = false,
}: Props) {
  const { toast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback(
    (newFiles: File[]) => {
      const { valid, errors } = validateFiles(newFiles, files.length);
      errors.forEach((msg) =>
        toast({ title: "⚠️ تنبيه", description: msg, variant: "destructive" })
      );
      if (valid.length > 0) {
        onFilesChange([...files, ...valid]);
      }
    },
    [files, onFilesChange, toast]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length) addFiles(selected);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length) addFiles(dropped);
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  return (
    <div className="space-y-3">
      {/* File list */}
      <AnimatePresence>
        {files.length > 0 && (
          <div className="space-y-2">
            {files.map((file, index) => {
              const isImage = isImageFile(file);
              return (
                <motion.div
                  key={`${file.name}-${index}-${file.size}`}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-3 bg-muted/50 rounded-xl p-2.5 border border-border"
                >
                  {isImage ? (
                    <div className="w-10 h-10 rounded-lg overflow-hidden border bg-muted shrink-0">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5 text-destructive" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(file.size)}
                      {isImage ? " • صورة" : " • ملف"}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 shrink-0 text-destructive hover:text-destructive"
                    onClick={() => removeFile(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </motion.div>
              );
            })}

            {/* Summary */}
            <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
              <span>
                {files.length} / {maxFiles} ملف
              </span>
              <span>{formatFileSize(totalSize)} إجمالي</span>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Drop zone */}
      {files.length < maxFiles && (
        <div
          className={`border-2 border-dashed rounded-xl text-center cursor-pointer transition-all ${
            compact ? "p-4" : "p-6"
          } ${
            isDragging
              ? "border-primary bg-primary/10 scale-[1.02]"
              : "border-muted-foreground/25 hover:border-primary/50"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {isDragging ? (
            <>
              <Upload className="w-8 h-8 mx-auto text-primary mb-1.5 animate-bounce" />
              <p className="text-sm font-medium text-primary">أفلت الملفات هنا</p>
            </>
          ) : files.length === 0 ? (
            <>
              <Upload
                className={`mx-auto text-muted-foreground mb-2 ${compact ? "w-7 h-7" : "w-10 h-10"}`}
              />
              <p className="text-sm font-medium text-muted-foreground">{label}</p>
              <p className="text-xs text-muted-foreground mt-1">
                PDF, صور — حتى 200MB لكل ملف — حتى {maxFiles} ملف
              </p>
            </>
          ) : (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Plus className="w-5 h-5" />
              <span>إضافة ملفات أخرى</span>
            </div>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFileInput}
        multiple
        {...(capture ? { capture: "environment" as any } : {})}
      />
    </div>
  );
}
